import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import { Coffee } from 'lucide-react'
import { PageMeta } from '@/components/ui/PageMeta'
import { HeroSimple } from '@/components/ui/HeroSimple'
import { BarMenuSection } from '@/components/bar/BarMenuSection'
import { AperitifsSection } from '@/components/bar/AperitifsSection'
import { Footer } from '@/components/layout/Footer'
import { LocalizedLink } from '@/components/LocalizedLink'
import { cn } from '@/lib/utils'
import { useBarMenuItems } from '@/hooks/useBarMenuItems'
import type { BarMenuItem } from '@/lib/barMenuItems'

export function Bar() {
  const { t, i18n } = useTranslation('bar')
  const shouldReduceMotion = useReducedMotion()
  const lang = i18n.language as 'it' | 'en'

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  const canonicalPath = `/${lang}/bar-e-tas`

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BarOrPub',
    name: 'Bar e Tas',
    image: 'https://maiaetas.it/media/logo-bar.jpg',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Via Ponte della Crema 1/2',
      addressLocality: 'Crema',
      addressRegion: 'CR',
      postalCode: '26013',
      addressCountry: 'IT',
    },
    telephone: '+39-333-702-4046',
    url: `https://maiaetas.it${canonicalPath}`,
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
        opens: '07:30',
        closes: '15:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Friday', 'Saturday'],
        opens: '07:30',
        closes: '15:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Friday', 'Saturday'],
        opens: '17:00',
        closes: '21:00',
      },
    ],
  }

  const breakfastFallback = t('breakfast.items', { returnObjects: true }) as BarMenuItem[]
  const lunchFallback = t('lunch.items', { returnObjects: true }) as BarMenuItem[]
  const aperitifFallback = t('aperitifs.items', { returnObjects: true }) as BarMenuItem[]

  const breakfastItems = useBarMenuItems('breakfast', breakfastFallback)
  const lunchItems = useBarMenuItems('lunch', lunchFallback)
  const aperitivoItems = useBarMenuItems('aperitifs', aperitifFallback)

  return (
    <>
      <PageMeta
        title={t('meta.title')}
        description={t('meta.description')}
        canonicalPath={canonicalPath}
        jsonLd={jsonLd}
        faviconVariant="bar"
      />

      <main id="main-content">
        <HeroSimple
          backgroundColor="bg-bar"
          textColor="text-ink"
          icon={Coffee}
          title={t('hero.title')}
          className="min-h-[50vh] md:min-h-[55vh]"
        />

        {/* Aperitivi */}
        <AperitifsSection
          id="aperitifs"
          label={t('aperitifs.label')}
          title={t('aperitifs.title')}
          text={t('aperitifs.text')}
          items={aperitivoItems}
        />

        {/* Colazione */}
        <BarMenuSection
          id="breakfast"
          title={t('breakfast.title')}
          description={t('breakfast.description')}
          items={breakfastItems}
          variant="dark"
        />

        {/* Pranzo */}
        <BarMenuSection
          id="lunch"
          title={t('lunch.title')}
          description={t('lunch.description')}
          items={lunchItems}
          variant="light"
          reverse
        />

        {/* CTA finale */}
        <section className="bg-bar px-4 py-16 text-center sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mx-auto max-w-2xl">
            <h2 className="text-[clamp(1.75rem,4vw,3rem)] font-bold leading-tight tracking-tight text-ink">
              {t('finalCta.title')}
            </h2>
            {t('finalCta.subtitle') && (
              <p className="mt-4 text-lg text-ink/80">
                {t('finalCta.subtitle')}
              </p>
            )}
            <LocalizedLink
              to="pizza"
              className={cn(
                'mt-8 inline-flex items-center justify-center rounded-full bg-white px-8 py-3',
                'text-sm font-semibold text-bar-dark transition-colors hover:bg-bar-light'
              )}
            >
              {t('finalCta.cta')}
            </LocalizedLink>
          </motion.div>
        </section>
      </main>

      <Footer variant="bar" />
    </>
  )
}
